import React, { useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../context/AuthContext'

export default function Settings() {
  const { profile } = useAuth()
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  async function handleChangePassword(e) {
    e.preventDefault()
    setError('')
    setSuccess('')
    if (newPassword !== confirmPassword) { setError('New passwords do not match'); return }
    if (newPassword.length < 6) { setError('Password must be at least 6 characters'); return }
    setLoading(true)

    // Get the email used for auth — could be placeholder for no-email employees
    const { data: { user } } = await supabase.auth.getUser()
    const authEmail = user?.email

    if (!authEmail) {
      setError('Could not find your account email. Please contact your admin.')
      setLoading(false)
      return
    }

    // Re-authenticate with current password
    const { error: signInError } = await supabase.auth.signInWithPassword({
      email: authEmail,
      password: currentPassword
    })
    if (signInError) { setError('Current password is incorrect'); setLoading(false); return }

    // Update to new password
    const { error: updateError } = await supabase.auth.updateUser({ password: newPassword })
    if (updateError) { setError(updateError.message); setLoading(false); return }

    setSuccess('Password updated successfully!')
    setCurrentPassword('')
    setNewPassword('')
    setConfirmPassword('')
    setLoading(false)
  }

  return (
    <div style={styles.page}>
      <h1 style={styles.title}>Settings</h1>
      <p style={styles.sub}>Manage your account preferences</p>

      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Your Profile</h2>
        <div style={styles.profileRow}>
          <div style={styles.avatar}>{(profile?.full_name || '?').charAt(0).toUpperCase()}</div>
          <div>
            <div style={styles.profileName}>{profile?.full_name}</div>
            <div style={styles.profileEmail}>{profile?.email || 'No email on file'}</div>
            <div style={styles.profileRole}>{profile?.role === 'admin' ? '🛡️ Admin' : '👷 Employee'}</div>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Change Password</h2>
        <p style={styles.cardSub}>Choose a strong password you'll remember.</p>
        <form onSubmit={handleChangePassword} style={styles.form}>
          {error && <div style={styles.error}>{error}</div>}
          {success && <div style={styles.successMsg}>{success}</div>}
          <div style={styles.field}>
            <label style={styles.label}>Current Password</label>
            <input style={styles.input} type="password" value={currentPassword} onChange={e => setCurrentPassword(e.target.value)} placeholder="Enter current password" required />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>New Password</label>
            <input style={styles.input} type="password" value={newPassword} onChange={e => setNewPassword(e.target.value)} placeholder="Min 6 characters" required />
          </div>
          <div style={styles.field}>
            <label style={styles.label}>Confirm New Password</label>
            <input style={styles.input} type="password" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Repeat new password" required />
          </div>
          <button style={styles.saveBtn} type="submit" disabled={loading}>
            {loading ? 'Updating...' : 'Update Password'}
          </button>
        </form>
      </div>
    </div>
  )
}

const styles = {
  page: { maxWidth: '680px' },
  title: { margin: '0 0 4px', fontSize: '28px', fontWeight: '700', color: '#1a2e1a' },
  sub: { margin: '0 0 24px', color: '#6b7c6b', fontSize: '14px' },
  card: { background: 'white', borderRadius: '12px', padding: '24px', marginBottom: '20px', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' },
  cardTitle: { margin: '0 0 4px', fontSize: '18px', fontWeight: '700', color: '#1a2e1a' },
  cardSub: { margin: '0 0 20px', fontSize: '13px', color: '#6b7c6b' },
  profileRow: { display: 'flex', alignItems: 'center', gap: '16px' },
  avatar: { width: '56px', height: '56px', borderRadius: '50%', background: '#2d4a2d', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '24px', fontWeight: '700', flexShrink: 0 },
  profileName: { fontWeight: '700', fontSize: '18px', color: '#1a2e1a', marginBottom: '2px' },
  profileEmail: { fontSize: '14px', color: '#6b7c6b', marginBottom: '4px' },
  profileRole: { fontSize: '13px', color: '#2d4a2d', fontWeight: '600' },
  form: { display: 'flex', flexDirection: 'column', gap: '16px' },
  field: { display: 'flex', flexDirection: 'column', gap: '6px' },
  label: { fontSize: '12px', fontWeight: '600', color: '#374151', textTransform: 'uppercase', letterSpacing: '0.5px' },
  input: { padding: '11px 14px', borderRadius: '8px', border: '1.5px solid #d1d5db', fontSize: '15px', fontFamily: 'inherit' },
  saveBtn: { padding: '13px', background: '#2d4a2d', color: 'white', border: 'none', borderRadius: '8px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', marginTop: '4px' },
  error: { background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', padding: '12px', borderRadius: '8px', fontSize: '14px' },
  successMsg: { background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#16a34a', padding: '12px', borderRadius: '8px', fontSize: '14px' },
}
